<template>
  <!-- Public standalone pages (e.g. customer tracker) render without the
       admin shell — no sidebar/header (US #18826, task 18978). -->
  <div v-if="isPublicRoute" class="h-screen w-full overflow-y-auto">
    <router-view />
  </div>

  <div
    v-else
    class="main h-screen min-h-0 w-full bg-[#f3f3f3] flex overflow-y-auto overflow-x-visible overflow-visible"
  >
    <Loader :loading="globalLoader" />
    <!-- Sidebar -->
    <Transition name="sidebar-slide overflow-visible">
      <Sidebar
        :navigation="navigation"
        :iconOnly="!sidebarOpen"
        class="transition-all duration-200 h-full overflow-visible"
        :class="sidebarOpen ? 'w-[320px]' : 'w-[85px]'"
      />
    </Transition>
    <div
      class="flex flex-col border-1 flex-1 h-full min-h-0 overflow-visible scroll-hidden"
      :class="{ mobileDevice: mobileDevice, desktop: !mobileDevice }"
      :style="{
        width: sidebarOpen ? 'calc(100vw - 320px)' : 'calc(100vw - 85px)',
      }"
    >
      <Header />
      <div
        class="h-full flex-1 w-full mx-auto heightClass overflow-y-auto min-h-0 overflow-visible"
      >
        <router-view />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import Header from './components/Header.vue';
import Sidebar from './components/Sidebar.vue';
import { useMainStore } from './store';
import Loader from './components/Shared/Loader.vue';

const store = useMainStore();
const { navigation, mobileDevice, sidebarOpen, globalLoader } = storeToRefs(store);
const route = useRoute();

/** Public/standalone routes render without the admin sidebar + header. */
const isPublicRoute = computed<boolean>(() => route.meta?.public === true);
const screenWidth = ref(window.innerWidth);
const screenHeight = ref(window.innerHeight);

const updateScreenSize = () => {
  screenWidth.value = window.innerWidth;
  screenHeight.value = window.innerHeight;
  mobileDevice.value = window.innerWidth <= 767;
};

onMounted(() => {
  updateScreenSize();
  window.addEventListener('resize', updateScreenSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenSize);
});
</script>

<style lang="scss">
.heightClass {
  height: calc(100vh - 53px);
}
.desktop {
  width: 100%;
}
.mobileDevice {
  width: 100vw;
}
.spinner {
  @apply border-primary;
  border-width: 5px;
  border-style: solid;
  border-top-color: transparent;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
}
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition:
    opacity 0.3s,
    transform 0.3s;
}
.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}
.sidebar-slide-enter-to,
.sidebar-slide-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
